import React, { useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';

const DEFAULT_FORM = {
  generationAuto: false,
  frequence: 'hebdomadaire',
  articlesParCycle: 2,
  heureGeneration: 9,
  langue: 'fr',
  nbSujets: 10,
  nbMotscles: 50,
};

const API_SERVICES = [
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'supabase', label: 'Supabase' },
  { id: 'unsplash', label: 'Unsplash' },
];

function Parametres() {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [apiState, setApiState] = useState({
    anthropic: { ok: false, loading: true, message: 'Test en cours...' },
    supabase: { ok: false, loading: true, message: 'Test en cours...' },
    unsplash: { ok: false, loading: true, message: 'Test en cours...' },
  });

  const updateField = (key, value) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const testService = async (serviceId) => {
    setApiState((current) => ({
      ...current,
      [serviceId]: { ...current[serviceId], loading: true, message: 'Test en cours...' },
    }));
    try {
      const response = await apiFetch(`/api/parametres/test/${serviceId}`);
      const data = await response.json();
      setApiState((current) => ({
        ...current,
        [serviceId]: {
          ok: Boolean(data?.ok && response.ok),
          loading: false,
          message: data?.message || (response.ok ? 'Connexion OK' : 'Connexion KO'),
        },
      }));
    } catch {
      setApiState((current) => ({
        ...current,
        [serviceId]: {
          ok: false,
          loading: false,
          message: 'Impossible de tester la connexion',
        },
      }));
    }
  };

  const loadSettings = async () => {
    try {
      const response = await apiFetch('/api/parametres');
      if (!response.ok) throw new Error('Erreur chargement paramètres');
      const data = await response.json();
      setFormData({
        generationAuto: Boolean(data?.generationAuto),
        frequence: data?.frequence || 'hebdomadaire',
        articlesParCycle: Number(data?.articlesParCycle || 2),
        heureGeneration: Number(data?.heureGeneration || 9),
        langue: data?.langue || 'fr',
        nbSujets: Number(data?.nbSujets || 10),
        nbMotscles: Number(data?.nbMotscles || 50),
      });
    } catch {
      setSaveMessage('Impossible de charger les paramètres.');
    }
  };

  useEffect(() => {
    loadSettings();
    API_SERVICES.forEach((service) => {
      testService(service.id);
    });
  }, []);

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      setSaveMessage('');
      const response = await apiFetch('/api/parametres', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Erreur sauvegarde paramètres');
      await response.json();
      setSaveMessage('Paramètres sauvegardés avec succès.');
    } catch {
      setSaveMessage('Impossible de sauvegarder les paramètres.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="parametres-page">
      <section className="parametres-card">
        <h2>Clés API</h2>
        <div className="parametres-services">
          {API_SERVICES.map((service) => {
            const state = apiState[service.id] || { ok: false, loading: false, message: '' };
            return (
              <article key={service.id} className="parametres-service-item">
                <div className="parametres-service-head">
                  <strong>{service.label}</strong>
                  <span className={`parametres-dot ${state.ok ? 'ok' : 'ko'}`} />
                </div>
                <p>{state.loading ? 'Test de connexion en cours...' : state.message || 'Non testé'}</p>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => testService(service.id)}
                  disabled={state.loading}
                >
                  {state.loading ? 'Test...' : 'Tester la connexion'}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="parametres-card">
        <h2>Génération automatique</h2>
        <div className="parametres-grid">
          <label className="parametres-row">
            <span>Activer la génération automatique</span>
            <input
              type="checkbox"
              checked={formData.generationAuto}
              onChange={(event) => updateField('generationAuto', event.target.checked)}
            />
          </label>

          <label className="parametres-row">
            <span>Fréquence</span>
            <select value={formData.frequence} onChange={(event) => updateField('frequence', event.target.value)}>
              <option value="quotidien">Quotidien</option>
              <option value="hebdomadaire">Hebdomadaire</option>
              <option value="mensuel">Mensuel</option>
            </select>
          </label>

          <label className="parametres-row">
            <span>Articles par cycle (1 à 5)</span>
            <select
              value={formData.articlesParCycle}
              onChange={(event) => updateField('articlesParCycle', Number(event.target.value))}
            >
              {[1, 2, 3, 4, 5].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </label>

          <label className="parametres-row">
            <span>Heure de génération préférée</span>
            <select
              value={formData.heureGeneration}
              onChange={(event) => updateField('heureGeneration', Number(event.target.value))}
            >
              {Array.from({ length: 24 }).map((_, hour) => (
                <option key={hour} value={hour}>
                  {hour}h
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="parametres-card">
        <h2>Préférences générales</h2>
        <div className="parametres-grid">
          <label className="parametres-row">
            <span>Langue par défaut</span>
            <select value={formData.langue} onChange={(event) => updateField('langue', event.target.value)}>
              <option value="fr">Français</option>
              <option value="en">Anglais</option>
            </select>
          </label>

          <label className="parametres-row">
            <span>Nombre de sujets par analyse</span>
            <select value={formData.nbSujets} onChange={(event) => updateField('nbSujets', Number(event.target.value))}>
              {[5, 10, 15, 20].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <label className="parametres-row">
            <span>Nombre de mots-clés par analyse</span>
            <select
              value={formData.nbMotscles}
              onChange={(event) => updateField('nbMotscles', Number(event.target.value))}
            >
              {[25, 50, 75, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="parametres-actions">
        <button type="button" className="primary-button" onClick={saveSettings} disabled={isSaving}>
          {isSaving ? 'Sauvegarde...' : 'Sauvegarder les préférences'}
        </button>
        {saveMessage && <p className="parametres-message">{saveMessage}</p>}
      </section>
    </section>
  );
}

export default Parametres;
